import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
  BookOpen, Download, Search, ChevronRight, ChevronLeft,
  Printer, ArrowUp, CheckCircle2
} from 'lucide-react';
import Logo from '../Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RetailerOnboardingGuide() {
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [completedSections, setCompletedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('guide-completed') || '[]');
    } catch { return []; }
  });
  const contentRef = useRef(null);

  useEffect(() => {
    fetchGuide();
  }, []);

  useEffect(() => {
    localStorage.setItem('guide-completed', JSON.stringify(completedSections));
  }, [completedSections]);

  const fetchGuide = async () => {
    try {
      const res = await fetch(`${API_URL}/api/retailer-copilot/knowledge-base`);
      if (res.ok) {
        const data = await res.json();
        setSections(data.sections || []);
        if (data.sections?.length) {
          setActiveSection(data.sections[0]);
        }
      }
    } catch (e) {
      console.error('Failed to fetch guide:', e);
    }
  };

  const toggleComplete = (sectionId) => {
    setCompletedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const navigateSection = (direction) => {
    if (!activeSection) return;
    const idx = sections.findIndex(s => s.id === activeSection.id);
    const nextIdx = direction === 'next' ? idx + 1 : idx - 1;
    if (nextIdx >= 0 && nextIdx < sections.length) {
      setActiveSection(sections[nextIdx]);
      contentRef.current?.scrollTo(0, 0);
    }
  };

  const handleDownload = () => {
    let content = `DEALSHAQ RETAILER ONBOARDING GUIDE — V1.1\n${'='.repeat(50)}\n\n`;
    content += `Generated: ${new Date().toLocaleDateString()}\n\n`;

    sections.forEach((section, i) => {
      content += `${'='.repeat(50)}\n`;
      content += `${i + 1}. ${section.title.toUpperCase()}\n`;
      content += `${'='.repeat(50)}\n\n`;
      content += `${section.content}\n\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'DealShaq-Retailer-Onboarding-Guide-V1.1.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredSections = sections.filter(s =>
    !searchQuery ||
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const progress = sections.length > 0
    ? Math.round((completedSections.length / sections.length) * 100)
    : 0;

  const renderContent = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n(\d+)\.\s/g, '<br/><strong class="text-purple-700">$1.</strong> ')
      .replace(/\n-\s/g, '<br/>&#8226; ')
      .replace(/\n/g, '<br/>');
  };

  const currentIdx = activeSection ? sections.findIndex(s => s.id === activeSection.id) : -1;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="retailer-onboarding-guide">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Logo size="small" className="hidden sm:block" />
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-gray-800 truncate">Retailer Onboarding Guide</h1>
              <p className="text-xs text-gray-500 hidden sm:block">V1.1 — Pilot Edition</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge variant="outline" className="text-xs hidden sm:inline-flex">
              {progress}% Complete
            </Badge>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="guide-print" className="hidden sm:flex">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={handleDownload} className="bg-purple-600 hover:bg-purple-700" data-testid="guide-download">
              <Download className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Download</span>
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-1 bg-purple-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        {/* Sidebar - Table of Contents */}
        <aside className={`${sidebarOpen ? 'w-72' : 'w-0'} bg-white border-r transition-all overflow-hidden print:hidden flex-shrink-0`}>
          <div className="p-4 w-72">
            <div className="mb-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input
                  data-testid="guide-search"
                  placeholder="Search guide..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 text-sm h-9"
                />
              </div>
            </div>

            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Table of Contents</p>
            <ScrollArea className="h-[calc(100vh-220px)]">
              <div className="space-y-1">
                {filteredSections.map((section, i) => {
                  const isActive = activeSection?.id === section.id;
                  const isCompleted = completedSections.includes(section.id);
                  const originalIdx = sections.findIndex(s => s.id === section.id);
                  return (
                    <button
                      key={section.id}
                      data-testid={`toc-${section.id}`}
                      onClick={() => {
                        setActiveSection(section);
                        contentRef.current?.scrollTo(0, 0);
                      }}
                      className={`w-full text-left p-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                        isActive
                          ? 'bg-purple-100 text-purple-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                        isCompleted
                          ? 'bg-emerald-500 text-white'
                          : isActive
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="w-3 h-3" /> : originalIdx + 1}
                      </div>
                      <span className="truncate">{section.title}</span>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </aside>

        {/* Toggle sidebar */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="print:hidden bg-white border-r border-y px-1 py-2 text-gray-400 hover:text-gray-600 self-start mt-4 rounded-r"
        >
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto" ref={contentRef}>
          {activeSection ? (
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <Badge variant="secondary" className="mb-2 text-xs">
                  Section {currentIdx + 1} of {sections.length}
                </Badge>
                <h2 className="text-2xl font-bold text-gray-900">{activeSection.title}</h2>
              </div>

              <Card className="mb-6">
                <CardContent className="p-6">
                  <div
                    className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: renderContent(activeSection.content) }}
                  />
                </CardContent>
              </Card>

              {/* Section Navigation + Mark Complete */}
              <div className="flex items-center justify-between print:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateSection('prev')}
                  disabled={currentIdx <= 0}
                  data-testid="guide-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>

                <Button
                  variant={completedSections.includes(activeSection.id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleComplete(activeSection.id)}
                  className={completedSections.includes(activeSection.id) ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  data-testid="guide-mark-complete"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {completedSections.includes(activeSection.id) ? 'Completed' : 'Mark Complete'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateSection('next')}
                  disabled={currentIdx >= sections.length - 1}
                  data-testid="guide-next"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Loading guide...</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
